import { Form, FormInstance, Input, Select, Tooltip } from 'antd';
import classnames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ArrowRightOutlined, DownOutlined, WarningOutlined } from '@ant-design/icons';
import { Icon } from '@components/metrics-builder/components/MetricList';
import DeleteActiveSvg from '@icons/mapping-panel/delete-active-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { useGetEventCategory, useGetMappedEvents } from '@src/api/emp/queryHooks';

import styles from './index.less';

export enum ERuleTip {
  MISMATCH = 'Pre-defined name and category mismatched',
  ILLEGAL_CHARACTER = 'Must contain only letters, numbers, spaces, and special characters: _, (, ), [, ], /, -, |.',
  EVENT_REACHED_SELECT_LIMIT = 'Unable to select more events, limit reached (50)',
  CHARACTER_LIMIT = 'Exceeds the maximum character limit (50)',
  EVENT_NAME_EXISTS = 'Unable to save, event name already exists.',
  EVENTS_REACHED_MAXIMUM_NUMBER = 'Unable to save, the maximum number of events that can be created (100) has been reached.',
  FORBIDDEN_PREFIX = `Cannot start with 'conviva_'`,
}

interface IReadyToMapItemData {
  raw_event: string;
  event_name: string;
  event_category: string;
}

interface IReadyToMapItem {
  active: boolean;
  onActive: (data: IReadyToMapItemData) => void;
  itemData: IReadyToMapItemData;
  name: number;
  restField: { fieldKey?: number | undefined };
  form: FormInstance;
  remove: () => void;
  repeatedNames: string[];
}

const renderOptionItem = (title: string, disabled: boolean) => {
  const color = disabled ? '#949494' : '#494949';
  const cursor = disabled ? 'not-allowed' : 'pointer';
  return {
    value: title,
    disabled: disabled,
    label: (
      <div
        style={{
          height: '100%',
          width: '100%',
          paddingLeft: '8px',
          display: 'flex',
          alignItems: 'center',
          color,
          cursor,
        }}
      >
        {title}
      </div>
    ),
  };
};

const ReadyToMapItem: React.FC<IReadyToMapItem> = ({
  name,
  restField,
  itemData,
  active,
  onActive,
  form,
  remove,
  repeatedNames,
}) => {
  const fieldsValue = Form.useWatch('events', form) ?? [];
  const { data } = useGetEventCategory();
  const { data: allMappedEvents } = useGetMappedEvents();

  const [greyCategory, setGreyCategory] = useState<boolean>(false);
  const [greyName, setGreyName] = useState<boolean>(false);
  const [editedCategory, setEditedCategory] = useState<boolean>(false);
  const [editedName, setEditedName] = useState<boolean>(false);
  const lastClickItem = useRef<'category' | 'name'>();

  useEffect(() => {
    repeatedNames?.length > 0 && form.validateFields([['events', name, 'event_name']]);
  }, [repeatedNames]);

  // TODO use validateMappedName
  // All the predefined names in the category
  const predefinedNames = useMemo(() => {
    const predefinedName: string[] = [];
    data?.data?.forEach((category) => {
      predefinedName.push(...(category?.predefined_event_names ?? []));
    });
    return predefinedName;
  }, [data]);

  // (Event name) contains illegal characters
  const containIllegalCharacters = useMemo(() => {
    // eslint-disable-next-line no-useless-escape
    const regex = /^[\u4e00-\u9fa5\w\s|()\[\]\-\/]+$/;
    return !regex.test(itemData?.event_name);
  }, [itemData]);

  // (Event name) exceeds the maximum character limit (50)
  const exceedMaximumCharacterLimit = useMemo(() => {
    return itemData?.event_name.length > 50;
  }, [itemData]);

  // The server already has this name
  const serverHaveRepeatedName = useMemo(() => {
    return (
      repeatedNames.some((name) => name === itemData?.event_name) ||
      allMappedEvents?.data?.find((mappedEvent) => mappedEvent.event_name === itemData?.event_name)
    );
  }, [repeatedNames, itemData, allMappedEvents]);

  // The front-end UI interface has the same name
  const frontendHaveRepeatedName = useMemo(() => {
    return fieldsValue.filter((field) => field.event_name === itemData?.event_name).length > 1;
  }, [fieldsValue]);

  // event_category and event_name mismatch
  const eventNameAndCategoryMismatch = useMemo(() => {
    const event_name = itemData?.event_name;
    const event_category = itemData?.event_category;
    return (
      event_name &&
      event_category &&
      !data?.data
        ?.find((category) => category?.category_name === event_category)
        ?.predefined_event_names?.includes(event_name) &&
      predefinedNames.includes(event_name)
    );
  }, [itemData, data, predefinedNames]);

  const containsForbiddenWords = useMemo(() => {
    return itemData?.event_name?.toLowerCase().startsWith('conviva_');
  }, [itemData]);

  const notConformRules = useMemo(() => {
    if (
      !greyName &&
      !serverHaveRepeatedName &&
      !frontendHaveRepeatedName &&
      !containIllegalCharacters &&
      !exceedMaximumCharacterLimit &&
      !containsForbiddenWords
    ) {
      return false;
    }
    return (
      greyName ||
      serverHaveRepeatedName ||
      frontendHaveRepeatedName ||
      containIllegalCharacters ||
      exceedMaximumCharacterLimit ||
      containsForbiddenWords
    );
  }, [
    greyName,
    serverHaveRepeatedName,
    frontendHaveRepeatedName,
    containIllegalCharacters,
    exceedMaximumCharacterLimit,
    containsForbiddenWords,
  ]);

  useEffect(() => {
    if (eventNameAndCategoryMismatch) {
      if (lastClickItem.current === 'name') {
        setGreyCategory(true);
        setGreyName(false);
      } else {
        setGreyCategory(false);
        setGreyName(true);
      }
    } else {
      setGreyCategory(false);
      setGreyName(false);
    }
  }, [eventNameAndCategoryMismatch, lastClickItem.current]);

  const categoryOptions = useMemo(() => {
    return data?.data?.map((v) => {
      return { label: v.category_name, value: v.category_name };
    });
  }, [data]);

  const mappedNameOptions = useMemo(() => {
    const options = data?.data
      ?.find((category) => category.category_name === itemData?.event_category)
      ?.predefined_event_names?.map((name) => {
        const pdfMappedEvent = allMappedEvents?.data?.find(
          (mappedEvent) => mappedEvent.event_name === name,
        );
        if (pdfMappedEvent) {
          return renderOptionItem(name, true);
        }
        return renderOptionItem(name, false);
      });
    if (!options || options.length === 0) {
      return [];
    }
    return [
      {
        label: (
          <div style={{ fontSize: '14px', paddingLeft: '8px', fontWeight: 'bold' }}>
            Predefined Name
          </div>
        ),
        options: options,
      },
    ];
  }, [data, itemData, allMappedEvents]);

  const onActiveItem = () => {
    onActive(itemData);
  };

  const handleDelete = () => {
    remove();
  };

  const Warning = (props) => (
    <Tooltip {...props}>
      <WarningOutlined style={{ color: '#faad14' }} />
    </Tooltip>
  );

  const Suffix = useMemo(() => {
    if (exceedMaximumCharacterLimit) {
      return <Warning key={ERuleTip.CHARACTER_LIMIT} title={ERuleTip.CHARACTER_LIMIT} />;
    }
    if (containsForbiddenWords) {
      return <Warning key={ERuleTip.FORBIDDEN_PREFIX} title={ERuleTip.FORBIDDEN_PREFIX} />;
    }
    if (containIllegalCharacters) {
      return <Warning key={ERuleTip.ILLEGAL_CHARACTER} title={ERuleTip.ILLEGAL_CHARACTER} />;
    }
    if (serverHaveRepeatedName || frontendHaveRepeatedName) {
      return <Warning key={ERuleTip.EVENT_NAME_EXISTS} title={ERuleTip.EVENT_NAME_EXISTS} />;
    }
    if (greyName) {
      return <Warning key={ERuleTip.MISMATCH} title={ERuleTip.MISMATCH} />;
    }
    return <span />;
  }, [
    exceedMaximumCharacterLimit,
    containIllegalCharacters,
    serverHaveRepeatedName,
    frontendHaveRepeatedName,
    greyName,
    containsForbiddenWords,
  ]);

  return (
    <div className={styles.readyToMapItem} onClick={onActiveItem}>
      <div className={styles.iconAndEventWrapper}>
        <div className={styles.eventWrapper}>
          <div className={styles.eventItem} style={{ justifyContent: 'space-between' }}>
            <div className={styles.nameWrapper}>
              <div className={classnames(styles.rowNumber, { [styles.rowNumberActive]: active })}>
                {name + 1}
              </div>
              <div className={styles.nameText}>{itemData.raw_event}</div>
            </div>
            <div className={styles.arrowIcon}>
              <ArrowRightOutlined />
            </div>
          </div>
          <span style={{ width: '32px' }} />
          <div className={styles.eventItem}>
            <Form.Item
              style={{ width: '100%' }}
              name={[name, 'event_category']}
              {...restField}
              validateStatus={greyCategory ? 'warning' : ''}
              rules={[
                () => ({
                  validator: async (_) => {
                    if (greyCategory) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select
                className={classnames({
                  [styles.selectSelectionItemEdited]: editedCategory,
                  [styles.selectSelectionItemActive]: active,
                  [styles.selectSelectionItemGrey]: greyCategory,
                })}
                placeholder="Please Select..."
                options={categoryOptions}
                showArrow={active}
                bordered={active}
                onChange={() => {
                  lastClickItem.current = 'category';
                  if (!editedCategory) {
                    setEditedCategory(true);
                  }
                  form.validateFields([['events', name, 'event_name']]);
                }}
                suffixIcon={
                  greyCategory ? (
                    <Warning key={ERuleTip.MISMATCH} title={ERuleTip.MISMATCH} />
                  ) : (
                    <DownOutlined />
                  )
                }
              />
            </Form.Item>
          </div>
          <span style={{ width: '32px' }} />
          <div className={styles.eventItem}>
            <Form.Item
              style={{ width: '100%' }}
              name={[name, 'event_name']}
              {...restField}
              validateStatus={notConformRules ? 'warning' : ''}
              rules={[
                () => ({
                  validator(_) {
                    if (notConformRules) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                style={{ width: '100%' }}
                className={classnames({
                  [styles.autoCompleteInputEdited]: editedName,
                  [styles.autoCompleteInputActive]: active,
                  [styles.autoCompleteInputGrey]: greyName,
                  [styles.autoCompleteInputWarning]: notConformRules,
                })}
                placeholder="Please input..."
                bordered={active}
                suffix={Suffix}
                onChange={() => {
                  lastClickItem.current = 'name';
                  if (!editedName) {
                    setEditedName(true);
                  }
                  form.validateFields([['events', name, 'event_category']]);
                }}
              />
            </Form.Item>
            <div className={styles.arrowIcon} onClick={handleDelete} style={{ cursor: 'pointer' }}>
              <Icon component={DeleteSvg} activeComponent={DeleteActiveSvg} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadyToMapItem;
